import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldCheck, Mail, Lock, User } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

const field = { width: '100%', background: '#0A0A0A', color: '#fff', border: '1px solid #3F3F46', padding: '10px 12px', fontSize: 14, boxSizing: 'border-box' };
const label = { display: 'block', fontSize: 11, color: '#A1A1AA', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 6 };

export default function SuperAdminSetup() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');
  const [f, setF] = useState({ email: '', name: '', password: '', confirm: '' });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (!token) { setError('This link is missing its token.'); return; }
    axios.get('/public/superadmin-setup', { params: { token } }).then((r) => setInfo(r.data)).catch((e) => setError(e?.response?.data?.detail || 'This setup link is invalid or expired.'));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (f.password.length < 12) return toast.error('Use at least 12 characters');
    if (f.password !== f.confirm) return toast.error('Passwords do not match');
    setBusy(true);
    try {
      const { data } = await axios.post('/public/superadmin-setup', { token, email: f.email, name: f.name, password: f.password });
      setDone(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not create the account');
      setBusy(false);
    }
  };

  return (
    <div data-testid="superadmin-setup-page" style={{ minHeight: '100vh', background: '#0A0A0A', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, fontFamily: 'Inter, system-ui, sans-serif' }}>
      <div style={{ width: '100%', maxWidth: 460, background: '#141414', border: '1px solid #27272A', borderTop: '3px solid #D90000', padding: 32 }}>
        <ShieldCheck size={28} color="#D90000" />
        <p style={{ ...label, marginTop: 14 }}>{info?.platform || 'DecoWorkflow'} · Platform control</p>
        <h1 style={{ fontSize: 24, fontWeight: 800, margin: '0 0 8px' }}>Create your Super Admin account</h1>
        {error && <p data-testid="superadmin-setup-error" style={{ color: '#f87171', fontSize: 14 }}>{error}</p>}
        {done && (
          <div data-testid="superadmin-setup-done">
            <p style={{ color: '#22c55e', fontSize: 14 }}>Account created for <b>{done.email}</b>. You can sign in now.</p>
            <a href="/login" data-testid="superadmin-setup-login-link" style={{ display: 'inline-block', marginTop: 10, background: '#D90000', color: '#fff', padding: '10px 16px', fontWeight: 700, fontSize: 13, textDecoration: 'none', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Go to Super Admin login</a>
          </div>
        )}
        {info && !done && (
          <form onSubmit={submit}>
            <p style={{ color: '#A1A1AA', fontSize: 13, lineHeight: 1.5 }}>Invited by {info.invited_by || 'an existing super admin'}. This link is single-use and expires at {new Date(info.expires_at).toLocaleTimeString()}. Choose the email and password you want to use for the platform console.</p>
            <div style={{ marginTop: 16 }}><label style={label}><Mail size={11} /> Email (your new username)</label><input data-testid="sa-setup-email" type="email" autoComplete="username" style={field} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required /></div>
            <div style={{ marginTop: 14 }}><label style={label}><User size={11} /> Name</label><input data-testid="sa-setup-name" style={field} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Optional" /></div>
            <div style={{ marginTop: 14 }}><label style={label}><Lock size={11} /> Password (min 12 characters)</label><input data-testid="sa-setup-password" type="password" autoComplete="new-password" style={field} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} required /></div>
            <div style={{ marginTop: 14 }}><label style={label}><Lock size={11} /> Confirm password</label><input data-testid="sa-setup-confirm" type="password" autoComplete="new-password" style={field} value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} required /></div>
            <button type="submit" data-testid="sa-setup-submit" disabled={busy} style={{ marginTop: 20, width: '100%', background: '#D90000', color: '#fff', border: 'none', padding: 12, fontWeight: 800, fontSize: 13, letterSpacing: '0.06em', textTransform: 'uppercase', cursor: 'pointer', opacity: busy ? 0.6 : 1 }}>{busy ? 'Creating…' : 'Create account'}</button>
          </form>
        )}
      </div>
    </div>
  );
}
